import 'client-only';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ConsentToContactApi,
  contract,
  type ClientInferRequest,
} from '@pie/consent-to-contact-sdk';
import { useAuthContext } from '@/components/AuthContext';
import { env } from '@/config/env';
import { ApiError } from '@/lib/api/ApiError';
import type { ConsentStateResponse, SubmitConsentsBody } from './types';

export * from './types';

const { client } = new ConsentToContactApi({ environment: env.NEXT_PUBLIC_PIE_ENV });

const QUERY_KEYS = { CONSENTS: 'consents' } as const;

export const queryKeys = {
  myConsents: () => ['userManagement', QUERY_KEYS.CONSENTS, 'me'] as const,
};

type SessionHeaders = { authorization: string };

export const getMyConsents = async (headers: SessionHeaders): Promise<ConsentStateResponse> => {
  const response = await client.getMyConsents({ extraHeaders: headers });

  if (response.status !== 200) {
    throw new ApiError(response.status, response.body, response.headers);
  }

  return response.body;
};

export const submitConsents = async (
  body: SubmitConsentsBody,
  headers: SessionHeaders,
  idempotencyKey?: string
): Promise<ConsentStateResponse> => {
  const response = await client.postConsents({
    body: body as ClientInferRequest<typeof contract.postConsents>['body'],
    headers: idempotencyKey ? { 'idempotency-key': idempotencyKey } : {},
    extraHeaders: headers,
  });

  if (response.status !== 201) {
    throw new ApiError(response.status, response.body, response.headers);
  }

  return response.body;
};

export const useMyConsents = ({ enabled = false }: { enabled?: boolean } = {}) => {
  const { getSessionHeaders } = useAuthContext();

  return useQuery({
    enabled,
    queryKey: queryKeys.myConsents(),
    queryFn: async () => {
      const { authorization } = await getSessionHeaders();
      return getMyConsents({ authorization });
    },
    // The modal is suppressed entirely on failure (UI-5), so retrying beyond
    // once just delays that decision without changing the outcome.
    retry: 1,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

export const useSubmitConsents = () => {
  const { getSessionHeaders } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      body,
      idempotencyKey,
    }: {
      body: SubmitConsentsBody;
      idempotencyKey?: string;
    }) => {
      const { authorization } = await getSessionHeaders();
      return submitConsents(body, { authorization }, idempotencyKey);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.myConsents(), data);
    },
  });
};
